import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import appTheme from 'theme';

import theme from 'shopper/themes';

import { getFromLocalStorage, setLocalStorage } from 'lib/localStorage';
import { parseToBool } from 'lib/string';

import LOCAL_STORAGE from 'constants/localStorage';

const { IS_DARK_MODE_ACTIVE } = LOCAL_STORAGE;

const DARK_THEME = 'dark';
const LIGHT_THEME = 'light';
const BASE_COLORS_INITIAL_KEY = 'base';

const ThemeContext = createContext({
  theme: LIGHT_THEME,
  setTheme: () => {},
  getColorHexFromToken: () => {},
  createMediaQueries: () => {},
});

ThemeContext.displayName = 'ThemeContext';

const mountMediaQuery = ({ selector = '&', mediaQuery, prop, value }) => ({
  [mediaQuery]: {
    [selector]: {
      [prop]: value,
    },
  },
});

const ThemeProvider = ({ children }) => {
  // Default theme set to 'light'
  const [themeName, setThemeName] = useState(LIGHT_THEME);

  const setTheme = (name) => {
    setLocalStorage(
      IS_DARK_MODE_ACTIVE,
      name === DARK_THEME ? 'true' : 'false'
    );
    setThemeName(name);
  };

  useEffect(() => {
    const isDarkModeActive =
      getFromLocalStorage(IS_DARK_MODE_ACTIVE) &&
      parseToBool(getFromLocalStorage(IS_DARK_MODE_ACTIVE));

    setTheme(isDarkModeActive ? DARK_THEME : LIGHT_THEME);
  }, []);

  const themeObj = { ...theme.promobit[themeName], ...appTheme[themeName] };

  const createMediaQueries = useCallback(
    ({ selector, prop, responsiveValues }) => {
      const { mediaQueries } = themeObj;

      // workaround to emotion accept props in camelCase in specific scenarios
      if (!Array.isArray(responsiveValues)) {
        return mountMediaQuery({
          mediaQuery: '@media screen and (min-width: 1rem)',
          selector,
          prop,
          value: responsiveValues,
        });
      }

      const mountedMediaQueries = Object.values(mediaQueries).map(
        (mediaQuery, index) => {
          const value = responsiveValues[index];

          return value
            ? mountMediaQuery({
                mediaQuery,
                selector,
                prop,
                value,
              })
            : null;
        }
      );

      return mountedMediaQueries
        .filter(Boolean)
        .reduce((prev, curr) => ({ ...prev, ...curr }), {});
    },
    []
  );

  const getColorHexFromToken = useCallback(
    async (token) => {
      const { getValueFromPath } = await import('lib/object');

      const isBaseColorToken = (colorToken) =>
        colorToken.startsWith(BASE_COLORS_INITIAL_KEY);

      const toAppColorToken = (colors, colorToken) =>
        getValueFromPath(colors, colorToken);

      const colorToken = !isBaseColorToken(token)
        ? toAppColorToken(themeObj.app, token)
        : token;

      return getValueFromPath(themeObj.colors, colorToken);
    },
    [themeObj]
  );

  return (
    <EmotionThemeProvider theme={themeObj}>
      <ThemeContext.Provider
        value={{
          theme: themeName,
          setTheme,
          getColorHexFromToken,
          createMediaQueries,
        }}
      >
        {children}
      </ThemeContext.Provider>
    </EmotionThemeProvider>
  );
};

const useTheme = () => useContext(ThemeContext);

ThemeProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default ThemeProvider;

export { useTheme };
